import { useEffect, useRef, useState } from 'react';
import { Frame } from './Frame';
import { NameTag } from './WhoAmI';
import { Darkroom, PrintOnPeg } from './Reveal';
import { StoryReveal } from './StoryPrint';
import { getStoryboard, type Storyboard } from '../lib/storyboard';
import { castMe, declineCast, dropCastPhoto, markCastOpened, starring } from '../lib/cast';
import { loadProfile, saveProfile } from '../lib/state';
import { writeUserDoc } from '../lib/feed';
import { dreamLabels, dreamPhrase } from '../data/intro-copy';
import { savePicture } from '../lib/share';
import { track } from '../analytics';

/**
 * Be in your story (well/product/32 §2), the screens:
 *
 *  - CastAsk: under the open story page, in place of the way on. The teller in
 *    panel 1 is ringed (StoryPage `outline`); Kraben asks, one line.
 *  - CastFlow: the HELLO sticker (name + the front camera, the privacy line
 *    always there), then the recast: the print goes back in the tray, the
 *    Moon redraws it, and it hangs face-down with a gold edge, "Starring Sam".
 *    Turned over, the plush page is wiped away panel by panel by the page with
 *    you in it, then "This is you now".
 *
 * On the reward, the Day 1 idea print is turned over while the crew draw
 * (`onInterlude`); anywhere else the darkroom covers the wait, and leaving it
 * is fine: the print waits on Home.
 */

export function CastAsk({ onYes, onNo }: { onYes: () => void; onNo: () => void }) {
  useEffect(() => { track('cast_offered'); }, []);
  return (
    <div className="cast-ask">
      <div className="cast-ask-say">
        <p className="bubble cast-ask-bubble"><b>Want to be in it?</b>The crew drew someone to tell your story. They’d rather draw you.</p>
        <img className="cast-ask-kraben" src="/art/reveal/kraben-peek.webp" alt="" draggable={false} />
      </div>
      <button className="btn butter" onClick={() => { track('cast_yes'); onYes(); }}>Put me in it</button>
      <button className="link cast-ask-no" onClick={() => { track('cast_not_now'); onNo(); }}>Not now</button>
    </div>
  );
}

/** Why the crew could not take it, in words a person can act on. */
function why(code?: string): string {
  if (code === 'crew_off') return 'Your crew is off, so nothing is drawn from a photo. You can turn them on from Friends.';
  if (code === 'busy') return 'They are still drawing you. One moment.';
  if (code === 'bad_photo') return 'That photo would not go. Try another?';
  if (code === 'too_many') return 'That is as many goes as this story gets.';
  return 'Could not reach the crew just now. Try again?';
}

/** Hello, my name is (step 3, and 3a: once, the gentle second ask for a photo). */
export function CastTag({ sb, onSent, onBack }: { sb: Storyboard; onSent: (r: { photo: boolean; sb: Storyboard }) => void; onBack: () => void }) {
  const profile = loadProfile();
  const [name, setName] = useState(profile.name || '');
  const [selfie, setSelfie] = useState<string>();
  const [from, setFrom] = useState<'camera' | 'library'>();
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [asked, setAsked] = useState<'no' | 'asking' | 'done'>('no');
  const [want, setWant] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const goals = dreamLabels(profile.dreams);
  // backing out of the sticker is a "Not now" too: it counts toward the one time it comes back
  const back = () => { track('cast_not_now', { from: 'tag' }); declineCast(); onBack(); };

  const send = async (withPhoto: boolean) => {
    const n = name.trim().slice(0, 40);
    if (!n || sending || busy) return;
    if (withPhoto && !selfie && asked === 'no') { setAsked('asking'); track('cast_photo_nudged'); return; }
    setSending(true); setErr(null);
    // the name is theirs everywhere, as it is on Day 2's sticker: a friend's link says it too
    if (n !== loadProfile().name) { saveProfile({ ...loadProfile(), name: n }); writeUserDoc({ name: n }).catch(() => {}); }
    const photo = withPhoto ? selfie : undefined;
    track('cast_name', { photo: !!photo });
    if (photo) track('cast_photo', { cast_photo_source: from });
    try {
      const next = await castMe(sb.storyId, { name: n, selfie: photo, goals: dreamPhrase(profile.dreams) });
      onSent({ photo: !!photo, sb: next });
    } catch (e) {
      setErr(why((e as { code?: string }).code));
      setSending(false);
    }
  };

  return (
    <Frame onBack={back} left={<span className="tag">Day {sb.day} · your story</span>}>
      <div className={`cast-tag ${want ? 'want-photo' : ''}`}>
        <div className="who-ask">
          <img className="cast-tag-kraben" src="/art/reveal/kraben-peek.webp" alt="" draggable={false} />
          <p className="who-bubble">Hello! Who’s telling it?<small>Your name, and a photo so they can draw you.</small></p>
        </div>
        <NameTag value={name} onChange={setName} goals={goals} selfie={selfie} camera source="cast"
          onSelfie={(s, f) => { setSelfie(s); setFrom(f); setWant(false); }} onBusy={setBusy} />
        <p className="cast-privacy">Only the crew sees your photo. They draw you from it, and it is never posted. Take it off any time.</p>
        {err && <p className="hint" role="alert" style={{ textAlign: 'center' }}>{err}</p>}
        {asked === 'asking' && !selfie ? (
          <div className="cast-nudge rise">
            <p>Without a photo they’ll draw someone who has your name. Add one?</p>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn small butter" style={{ flex: 1 }} onClick={() => { setAsked('done'); setWant(true); track('cast_photo_nudge', { took: 'photo' }); }}>Add a photo</button>
              <button className="btn small ghost" style={{ flex: 1 }} disabled={sending} onClick={() => { setAsked('done'); track('cast_photo_nudge', { took: 'name' }); void send(false); }}>Just my name</button>
            </div>
          </div>
        ) : (
          <div className="who-foot">
            <button className="btn coral" disabled={!name.trim() || busy || sending} onClick={() => send(true)}>
              {busy ? 'Adding your photo…' : sending ? 'One moment…' : selfie || asked !== 'done' ? 'Draw me in' : 'Just my name'}
            </button>
            <button className="link" onClick={back}>Not now</button>
          </div>
        )}
      </div>
    </Frame>
  );
}

type CastPhase = 'tag' | 'recasting' | 'casting' | 'ready' | 'reveal' | 'you' | 'failed';

/** How long the print sits in the tray before the next thing: long enough to see it go back in. */
const RECAST_MS = 2600;

/**
 * The cast, from the name tag (or from a drawing already under way) to "This
 * is you now". `onInterlude` hands the wait back to the reward, which turns the
 * idea print over meanwhile and comes back with `start="casting"`.
 */
export function CastFlow({ sb: first, start, onDone, onInterlude, wipeFrom: wipeFirst }: { sb: Storyboard; start: 'tag' | 'casting' | 'ready'; onDone: () => void; onInterlude?: (sb: Storyboard) => void; /** the page the new one wipes across (the plush one, the first time) */ wipeFrom?: string }) {
  const [sb, setSb] = useState(first);
  const [phase, setPhase] = useState<CastPhase>(start);
  const [err, setErr] = useState<string | null>(null);
  const wipe = useRef(wipeFirst ?? first.beforePageUrl ?? first.pageUrl);
  const name = sb.cast?.name || loadProfile().name;
  useEffect(() => { window.scrollTo(0, 0); }, [phase]);

  // the print goes back into the tray, then the wait is the reward's (the idea print) or the darkroom's
  useEffect(() => {
    if (phase !== 'recasting') return;
    const t = setTimeout(() => { if (onInterlude) onInterlude(sb); else setPhase('casting'); }, RECAST_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // drawing: ask until it is dry or did not come out
  useEffect(() => {
    if (phase !== 'casting') return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      const s = await getStoryboard(sb.storyId).catch(() => undefined);
      if (stop) return;
      if (s?.cast?.status === 'cast' && s.pageUrl !== wipe.current) { setSb(s); setPhase('ready'); return; }
      if (s?.cast?.status === 'failed') { setSb(s); setPhase('failed'); return; }
      if (++tries < 120) setTimeout(tick, 2500);
    };
    void tick();
    return () => { stop = true; };
  }, [phase, sb.storyId]);

  const redraw = async () => {
    track('cast_redraw', { after: phase });
    setErr(null);
    try {
      const next = await castMe(sb.storyId, { redo: true });
      wipe.current = sb.pageUrl;
      setSb(next);
      setPhase('casting');
    } catch (e) { setErr(why((e as { code?: string }).code)); }
  };

  if (phase === 'tag') {
    return <CastTag sb={sb} onBack={onDone} onSent={({ photo, sb: next }) => { setSb(next); if (photo) setPhase('recasting'); else onDone(); }} />;
  }
  if (phase === 'recasting' || phase === 'casting') {
    return (
      <Darkroom day={sb.day} words={starring(name)} says="Redrawing it with you in it"
        wait="Takes about a minute. If you go on, it waits on Home, face-down."
        onLeave={() => { track('cast_wait_left'); onDone(); }} />
    );
  }
  if (phase === 'ready') {
    return (
      <PrintOnPeg day={sb.day} words={starring(name)} gold line={sb.cast?.name ? `You’re in it, ${sb.cast.name}.` : 'You’re in it.'}
        onTurn={() => { track('cast_print_opened'); markCastOpened(sb); setPhase('reveal'); }} />
    );
  }
  if (phase === 'reveal') {
    return (
      <StoryReveal sb={sb} wipeFrom={wipe.current} doneLabel="Keep going" onDone={() => setPhase('you')}
        kicker={`Storyboarded by the Moon · starring ${name || 'you'}`}
        extra={<CastExtras sb={sb} onRedraw={redraw} err={err} />} />
    );
  }
  if (phase === 'you') return <ThisIsYou meUrl={sb.cast?.meUrl} onDone={onDone} />;

  const blocked = sb.cast?.blocked === 'billing';
  return (
    <Frame onBack={onDone} left={<span className="tag">Day {sb.day} · your story</span>}>
      <h1>{blocked ? 'Your crew are out of ink just now.' : 'The drawing of you did not come out.'}</h1>
      <p className="lede">{blocked ? 'Your story and your name are kept. They will draw you when they are back.' : 'Your story is as it was. They can have another go from the same photo.'}</p>
      <div className="stack" style={{ marginTop: 'auto' }}>
        {err && <p className="hint" role="alert">{err}</p>}
        {sb.cast?.canRedraw && !blocked && <button className="btn" onClick={redraw}>Try again</button>}
        <button className="btn ghost" onClick={onDone}>Keep going</button>
      </div>
    </Frame>
  );
}

/** Under the new page: Kraben approves, a save for showing off, and "Not quite me?" while there is a redraw left. */
export function CastExtras({ sb, onRedraw, err }: { sb: Storyboard; onRedraw: () => void; err: string | null }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!sb.pageUrl) return;
    setSaving(true);
    try { const outcome = await savePicture(sb.pageUrl, 'kindling-starring-me.jpg'); track('cast_shared', { outcome }); } finally { setSaving(false); }
  };
  return (
    <div className="cast-extras">
      <p className="cast-better"><img src="/art/reveal/kraben-peek.webp" alt="" draggable={false} /><span className="bubble">That’s better.</span></p>
      <div className="row" style={{ gap: 10 }}>
        <button className="btn small ghost cast-light" style={{ flex: 1 }} onClick={save} disabled={saving}>{saving ? 'One moment' : 'Save it'}</button>
        {sb.cast?.canRedraw && <button className="btn small ghost cast-light" style={{ flex: 1 }} onClick={onRedraw}>Not quite me?</button>}
      </div>
      {err && <p className="hint cast-err" role="alert">{err}</p>}
    </div>
  );
}

/** Step 6: the drawing of them alone, and what it is for from now on. */
export function ThisIsYou({ meUrl, onDone }: { meUrl?: string; onDone: () => void }) {
  const [off, setOff] = useState<'no' | 'busy' | 'done'>('no');
  const takeOff = async () => {
    setOff('busy');
    try { await dropCastPhoto(); setOff('done'); track('cast_photo_dropped', { from: 'you' }); } catch { setOff('no'); }
  };
  return (
    <div className="stage cast-you" role="region" aria-label="This is you now">
      <div className="cast-you-inner">
        {meUrl && <div className="cast-you-pic rise"><img src={meUrl} alt="You, drawn by the crew" /></div>}
        <h1 className="cast-you-line rise" style={{ animationDelay: '.15s' }}>This is you now.</h1>
        <p className="lede rise" style={{ animationDelay: '.3s' }}>Whenever you’re in something, the crew draws you like this.</p>
        <div className="stack" style={{ marginTop: 'auto', gap: 8 }}>
          <button className="btn" onClick={onDone}>Keep going</button>
          {off === 'done'
            ? <span className="hint" style={{ textAlign: 'center' }}>Your photo is gone. The drawing stays.</span>
            : <button className="link" disabled={off === 'busy'} onClick={takeOff}>Your photo stays private. Take it off</button>}
        </div>
      </div>
    </div>
  );
}
